import { useEffect, useMemo, useState } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { EyeIcon, PencilIcon, TrashIcon, ClipboardDocumentListIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PageHeader, Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Badge from '@/components/ui/Badge';
import Switch from '@/components/ui/Switch';
import SportBadge from '@/components/ui/SportBadge';
import Dropdown from '@/components/ui/Dropdown';
import EmptyState from '@/components/ui/EmptyState';
import Pagination from '@/components/ui/Pagination';
import Modal from '@/components/ui/Modal';
import { toaster } from '@/components/ui/toaster';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { BulkDeleteBar, SelectAllHeader, SelectRowCell } from '@/components/ui/BulkDelete';
import { useRowSelection } from '@/lib/useRowSelection';
import { useDebounce } from '@/lib/useDebounce';
import { useSports, useThemesRef } from '@/features/reference/useReference';
import { deleteWorkout as deleteWorkoutById, type WorkoutListItem, type DeleteImpact } from '@/api/workoutsService';
import type { WorkoutPayload } from '@/api/workoutsService';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import { useWorkouts, extractDeleteImpact } from './useWorkouts';
import WorkoutPreviewPanel from './WorkoutPreviewPanel';
import WorkoutModal from './WorkoutModal';
import WorkoutImportModal from './WorkoutImportModal';

interface Option {
  value: string;
  label: string;
}

const MultiSelectFilter = ({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: Option[];
  selected: string[];
  onToggle: (value: string) => void;
}) => (
  <Popover className="relative">
    <PopoverButton className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
      {label}
      {selected.length > 0 && <span className="rounded-full bg-brand-500 px-1.5 text-xs text-white">{selected.length}</span>}
      <ChevronDownIcon className="h-4 w-4" />
    </PopoverButton>
    <PopoverPanel className="absolute z-40 mt-1 max-h-64 w-56 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-800 dark:bg-gray-900">
      {options.length === 0 && <p className="px-2 py-1 text-sm text-gray-400">Aucune option</p>}
      {options.map((opt) => (
        <label
          key={opt.value}
          className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          <input
            type="checkbox"
            className="rounded border-gray-300"
            checked={selected.includes(opt.value)}
            onChange={() => onToggle(opt.value)}
          />
          {opt.label}
        </label>
      ))}
    </PopoverPanel>
  </Popover>
);

const rowId = (w: WorkoutListItem) => w.id;

const WorkoutsPage = () => {
  const [search, setSearch] = useState('');
  const [themeIds, setThemeIds] = useState<string[]>([]);
  const [sportIds, setSportIds] = useState<string[]>([]);
  const [testsOnly, setTestsOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WorkoutListItem | null>(null);
  const [deleteImpact, setDeleteImpact] = useState<DeleteImpact | null>(null);
  const [deleting, setDeleting] = useState(false);
  const debouncedSearch = useDebounce(search, 300);
  const queryClient = useQueryClient();

  const { data: themes = [] } = useThemesRef();
  const { data: sports = [] } = useSports();

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, themeIds, sportIds, testsOnly]);

  const filters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      themeIds: themeIds.length > 0 ? themeIds : undefined,
      sportIds: sportIds.length > 0 ? sportIds : undefined,
      testsOnly: testsOnly || undefined,
      page,
    }),
    [debouncedSearch, themeIds, sportIds, testsOnly, page]
  );

  const { items, total, pageSize, kpis, isLoading, deleteWorkout, createWorkout, updateWorkout, isSaving, invalidate } =
    useWorkouts(filters);
  const selection = useRowSelection(items, rowId, filters);

  const handleImported = () => {
    invalidate();
    queryClient.invalidateQueries({ queryKey: queryKeys.pv2WorkoutKpis });
  };

  const openCreate = () => {
    setEditId(null);
    setModalOpen(true);
  };
  const openEdit = (id: string) => {
    setEditId(id);
    setModalOpen(true);
  };
  const handleSubmit = (payload: WorkoutPayload) =>
    editId ? updateWorkout({ id: editId, payload }) : createWorkout(payload);

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  const themeLabel = (id: string) => themes.find((t) => t.id === id)?.label ?? id;
  const sportLabel = (id: string) => sports.find((s) => s.identifier === id)?.label ?? id;

  const hasFilters = Boolean(debouncedSearch) || themeIds.length > 0 || sportIds.length > 0;
  const resetFilters = () => {
    setSearch('');
    setThemeIds([]);
    setSportIds([]);
  };

  const openDelete = (workout: WorkoutListItem) => {
    setDeleteTarget(workout);
    setDeleteImpact(null);
  };
  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteImpact(null);
  };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteWorkout(deleteTarget.id);
      closeDelete();
    } catch (error) {
      const impact = extractDeleteImpact(error);
      if (impact) {
        setDeleteImpact(impact);
      } else {
        toaster.create({
          title: 'Suppression impossible',
          description: error instanceof Error ? error.message : 'Une erreur est survenue',
          type: 'error',
        });
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Bibliothèque de séances"
        description="Entrainement / Séances"
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              Importer un CSV
            </Button>
            <Button onClick={openCreate}>+ Ajouter une séance</Button>
          </div>
        }
      />

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <p className="text-sm text-gray-500 dark:text-gray-400">Total de séances</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{kpis?.total ?? '—'}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500 dark:text-gray-400">Séances structurées</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{kpis?.structured ?? '—'}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500 dark:text-gray-400">Brouillons</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{kpis?.drafts ?? '—'}</p>
        </Card>
      </div>

      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-72">
            <Input
              label="Rechercher"
              placeholder="Nom ou thème"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <MultiSelectFilter
            label="Thème"
            options={themes.map((t) => ({ value: t.id, label: t.label }))}
            selected={themeIds}
            onToggle={(v) => setThemeIds((prev) => toggle(prev, v))}
          />
          <MultiSelectFilter
            label="Sport"
            options={sports.map((s) => ({ value: s.identifier, label: s.label }))}
            selected={sportIds}
            onToggle={(v) => setSportIds((prev) => toggle(prev, v))}
          />
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <Switch checked={testsOnly} onChange={setTestsOnly} size="sm" />
            Tests uniquement
          </label>
          {hasFilters && (
            <Button variant="ghost" onClick={resetFilters}>
              Réinitialiser
            </Button>
          )}
        </div>

        {(themeIds.length > 0 || sportIds.length > 0) && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-400">Filtres actifs :</span>
            {themeIds.map((id) => (
              <button
                key={`t-${id}`}
                onClick={() => setThemeIds((prev) => prev.filter((v) => v !== id))}
                className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-600 dark:bg-brand-950 dark:text-brand-300"
              >
                {themeLabel(id)}
                <XMarkIcon className="h-3 w-3" />
              </button>
            ))}
            {sportIds.map((id) => (
              <button
                key={`s-${id}`}
                onClick={() => setSportIds((prev) => prev.filter((v) => v !== id))}
                className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300"
              >
                {sportLabel(id)}
                <XMarkIcon className="h-3 w-3" />
              </button>
            ))}
          </div>
        )}
      </Card>

      {isLoading && <p className="text-sm text-gray-500">Chargement…</p>}

      {!isLoading && items.length === 0 && (
        <EmptyState
          icon={<ClipboardDocumentListIcon />}
          title="Aucune séance"
          description="La bibliothèque est vide. Ajoutez une séance via la nomenclature."
        />
      )}

      {!isLoading && items.length > 0 && (
        <Card className="p-0">
          <BulkDeleteBar
            selection={selection}
            labelOf={(w) => w.name}
            remove={(w) => deleteWorkoutById(w.id)}
            onDone={handleImported}
          />
          <Table>
            <THead>
              <tr>
                <SelectAllHeader selection={selection} />
                <TH>Nom</TH>
                <TH>Sport</TH>
                <TH>Thème</TH>
                <TH>Ajoutée le ↓</TH>
                <TH className="text-right">Actions</TH>
              </tr>
            </THead>
            <TBody>
              {items.map((workout) => (
                <TR key={workout.id}>
                  <SelectRowCell selection={selection} row={workout} />
                  <TD className="font-bold text-gray-900 dark:text-gray-100">
                    <span className="inline-flex items-center gap-2">
                      {workout.name}
                      {workout.isDraft && <Badge color="yellow" className='font-medium'>Brouillon</Badge>}
                      {workout.isTest && <Badge color="blue" className="font-medium">Test</Badge>}
                    </span>
                  </TD>
                  <TD className="text-gray-500">
                    <SportBadge identifier={workout.sportIdentifier} label={workout.sportLabel} />
                  </TD>
                  <TD className="text-gray-500">{workout.themeLabel}</TD>
                  <TD className="text-gray-500">{new Date(workout.createdAt).toLocaleDateString('fr-FR')}</TD>
                  <TD className="text-right">
                    <Dropdown
                      actions={[
                        {
                          label: 'Aperçu',
                          icon: <EyeIcon className="h-4 w-4" />,
                          onClick: () => setPreviewId(workout.id),
                        },
                        {
                          label: 'Modifier',
                          icon: <PencilIcon className="h-4 w-4" />,
                          onClick: () => openEdit(workout.id),
                        },
                        {
                          label: 'Supprimer',
                          icon: <TrashIcon className="h-4 w-4" />,
                          destructive: true,
                          onClick: () => openDelete(workout),
                        },
                      ]}
                    />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
        </Card>
      )}

      <WorkoutPreviewPanel workoutId={previewId} onClose={() => setPreviewId(null)} />

      <WorkoutModal
        open={modalOpen}
        workoutId={editId}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        isSaving={isSaving}
      />

      <WorkoutImportModal open={importOpen} onClose={() => setImportOpen(false)} onImported={handleImported} />

      <Modal
        open={Boolean(deleteTarget)}
        onClose={closeDelete}
        title="Supprimer la séance"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={closeDelete}>
              {deleteImpact ? 'Fermer' : 'Annuler'}
            </Button>
            {!deleteImpact && (
              <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Suppression…' : 'Supprimer'}
              </Button>
            )}
          </>
        }
      >
        {deleteImpact ? (
          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
            <p>
              Suppression impossible : la séance <span className="font-semibold">« {deleteTarget?.name} »</span> est
              utilisée par :
            </p>
            <ul className="ml-4 list-disc space-y-1">
              {deleteImpact.cycles.map((c) => (
                <li key={`c-${c.id}`}>Cycle : {c.name}</li>
              ))}
              {deleteImpact.plans.map((p) => (
                <li key={`p-${p.id}`}>Plan : {p.name}</li>
              ))}
              {deleteImpact.athletePlans.map((a) => (
                <li key={`a-${a.id}`}>Plan d'athlète : {a.name}</li>
              ))}
            </ul>
            <p>Retirez-la d'abord de ces éléments avant de pouvoir la supprimer.</p>
          </div>
        ) : (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            La séance <span className="font-semibold">« {deleteTarget?.name} »</span> sera retirée des cycles et plans
            qui l'utilisent. Cette action est irréversible.
          </p>
        )}
      </Modal>
    </>
  );
};

export default WorkoutsPage;
